import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'text' | 'success' | 'gradient';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  loading = false,
  disabled,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  ...props
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-[#fad826] text-black border-[#fad826] hover:bg-transparent hover:text-[#fad826]';
      case 'secondary':
        return 'bg-transparent text-black border-black hover:bg-black hover:text-[#fad826]';
      case 'gradient':
        return 'bg-[#fad826] text-black border-[#fad826] hover:bg-transparent hover:text-[#fad826]';
      case 'outline':
        return 'bg-white/80 backdrop-blur-sm hover:bg-[#fad826] text-black border-[#fad826] hover:border-[#fad826]';
      case 'ghost':
        return 'bg-transparent hover:bg-[#fad826] text-black hover:text-black border-transparent';
      case 'danger':
        return 'bg-red-500 text-black border-red-500 hover:bg-transparent hover:text-red-500';
      case 'text':
        return 'bg-transparent hover:bg-[#fad826] text-black hover:text-[#fad826] border-transparent';
      case 'success':
        return 'bg-[#fad826] text-black border-[#fad826] hover:bg-transparent hover:text-[#fad826]';
      default:
        return 'bg-[#fad826] text-black border-[#fad826] hover:bg-transparent hover:text-[#fad826]';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return 'px-3 py-1.5 text-xs font-medium';
      case 'sm':
        return 'px-4 py-2 text-sm font-medium';
      case 'lg':
        return 'px-6 py-3 text-base font-semibold';
      case 'xl':
        return 'px-8 py-4 text-lg font-semibold';
      default:
        return 'px-5 py-2.5 text-sm font-medium';
    }
  };

  const baseClasses = `
    inline-flex items-center justify-center
    rounded-xl border
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-4 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    font-medium tracking-wide
    relative overflow-hidden
    ${fullWidth ? 'w-full' : ''}
  `.replace(/\s+/g, ' ').trim();

  return (
    <button
      className={`${baseClasses} ${getVariantClasses()} ${getSizeClasses()} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {/* Content */}
      <span className={`flex items-center gap-2 ${loading ? 'opacity-0' : 'opacity-100'}`}>
        {icon && iconPosition === 'left' && (
          <span className="flex-shrink-0">{icon}</span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="flex-shrink-0">{icon}</span>
        )}
      </span>

      {/* Shimmer effect for gradient buttons */}
      {(variant === 'gradient' || variant === 'primary' || variant === 'secondary') && !loading && (
        <div className="absolute inset-0 -top-2 -bottom-2 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out opacity-0 hover:opacity-100" />
      )}
    </button>
  );
};

export default Button;